// Enums — matches Форма 001/о PDF form

export type Sex = "male" | "female" | "unknown";
export type BloodType = "A" | "B" | "AB" | "O" | "unknown";
export type RhFactor = "positive" | "negative" | "unknown";
export type TriageCategory = "I" | "II" | "III" | "IV";
export type MedicalRole = 1 | 2 | 3 | 4;
export type FormRole = 1 | 2 | 11 | 12; // 1/2 = left column (page 1/2), 11/12 = right column (page 1/2)
export type TransportType = "casevac" | "mmpm" | "medevac" | "other";
export type TraumaType =
  | "gunshot"
  | "explosive"
  | "blunt"
  | "open"
  | "thermal_heat"
  | "thermal_cold"
  | "crush";
export type ConditionType =
  | "somatic"
  | "infectious"
  | "psychiatric"
  | "poisoning";
export type CbrnType = "chemical" | "biological" | "radiological" | "nuclear";
export type AnesthesiaType = "local" | "regional" | "general" | "sedation";
export type SurgeryType =
  | "craniectomy"
  | "laparotomy"
  | "thoracotomy"
  | "pericardiotomy"
  | "amputation"
  | "fasciotomy"
  | "escharotomy"
  | "debridement"
  | "vascularShunt"
  | "cantholysis"
  | "reboa"
  | "tracheostomy"
  | "suturing";
export type EvacuationCategory = "I" | "II" | "III";
export type Position = "sitting" | "lying";
export type AirwayStatus = "patent" | "partialObstruction" | "fullObstruction";
export type PulseStrength = "present" | "weak" | "absent";
export type UserRole =
  | "unauthorized"
  | "admin"
  | "medicRole1"
  | "medicRole2"
  | "medicRole3"
  | "coordinatorRole3"
  | "coordinatorRole4"
  | "medicRole4"
  | "tacticalMedicalOfficer"
  | "operationalMedicalOfficer"
  | "strategicMedicalOfficer";
export const USER_ROLE_VALUES: UserRole[] = [
  "unauthorized",
  "admin",
  "medicRole1",
  "medicRole2",
  "medicRole3",
  "medicRole4",
  "coordinatorRole3",
  "coordinatorRole4",
  "tacticalMedicalOfficer",
  "operationalMedicalOfficer",
  "strategicMedicalOfficer",
];

/** Roles that get hospital assignments (userHospitals junction) */
export const HOSPITAL_SCOPED_ROLES: UserRole[] = [
  "medicRole3",
  "coordinatorRole3",
  "coordinatorRole4",
  "medicRole4",
];

/**
 * Roles that get unit assignments (userUnits junction).
 * medicRole3 is included because "full visibility of subordinate units in their
 * designated area" requires unit-hierarchy access in addition to hospital access.
 */
export const UNIT_SCOPED_ROLES: UserRole[] = [
  "medicRole1",
  "medicRole2",
  "medicRole3",
  "medicRole4",
  "tacticalMedicalOfficer",
  "operationalMedicalOfficer",
];

/** Roles that get region assignments (userRegions junction). No region = global access. */
export const REGION_SCOPED_ROLES: UserRole[] = [
  "operationalMedicalOfficer",
  "strategicMedicalOfficer",
];
export type BodySide = "front" | "back";
export type TourniquetLimb = "rue" | "lue" | "rle" | "lle";
export type AllergyStatus = "yes" | "no" | "unknown";

export const SEX_VALUES: Sex[] = ["male", "female", "unknown"];
export const BLOOD_TYPE_VALUES: BloodType[] = ["A", "B", "AB", "O", "unknown"];
export const RH_FACTOR_VALUES: RhFactor[] = ["positive", "negative", "unknown"];
export const TRIAGE_CATEGORY_VALUES: TriageCategory[] = [
  "I",
  "II",
  "III",
  "IV",
];
export const MEDICAL_ROLE_VALUES: MedicalRole[] = [1, 2, 3, 4];
export const FORM_ROLE_VALUES: FormRole[] = [1, 2, 11, 12];
export const TRANSPORT_TYPE_VALUES: TransportType[] = [
  "casevac",
  "mmpm",
  "medevac",
];
export const TRAUMA_TYPE_VALUES: TraumaType[] = [
  "gunshot",
  "explosive",
  "blunt",
  "open",
  "thermal_heat",
  "thermal_cold",
  "crush",
];
export const CONDITION_TYPE_VALUES: ConditionType[] = [
  "somatic",
  "infectious",
  "psychiatric",
  "poisoning",
];
export const CBRN_TYPE_VALUES: CbrnType[] = [
  "chemical",
  "biological",
  "radiological",
  "nuclear",
];
export const ANESTHESIA_TYPE_VALUES: AnesthesiaType[] = [
  "local",
  "regional",
  "general",
  "sedation",
];
export const SURGERY_TYPE_VALUES: SurgeryType[] = [
  "craniectomy",
  "laparotomy",
  "thoracotomy",
  "pericardiotomy",
  "amputation",
  "fasciotomy",
  "escharotomy",
  "debridement",
  "vascularShunt",
  "cantholysis",
  "reboa",
  "tracheostomy",
  "suturing",
];
export const EVACUATION_CATEGORY_VALUES: EvacuationCategory[] = [
  "I",
  "II",
  "III",
];
export const POSITION_VALUES: Position[] = ["sitting", "lying"];
export const AIRWAY_STATUS_VALUES: AirwayStatus[] = [
  "patent",
  "partialObstruction",
  "fullObstruction",
];
export const PULSE_STRENGTH_VALUES: PulseStrength[] = [
  "present",
  "weak",
  "absent",
];
export const TOURNIQUET_LIMB_VALUES: TourniquetLimb[] = [
  "rue",
  "lue",
  "rle",
  "lle",
];
export const ALLERGY_STATUS_VALUES: AllergyStatus[] = ["yes", "no", "unknown"];

export const MILITARY_RANK_VALUES = [
  "Солдат",
  "Ст. солдат",
  "Мл. сержант",
  "Сержант",
  "Ст. сержант",
  "Гл. сержант",
  "Шт. сержант",
  "Майстер-сержант",
  "Ст. майстер-сержант",
  "Гл. майстер-сержант",
  "Мл. лейтенант",
  "Лейтенант",
  "Ст. лейтенант",
  "Капітан",
  "Майор",
  "Підполковник",
  "Полковник",
  "Бригадний генерал",
  "Генерал-майор",
  "Генерал-лейтенант",
  "Генерал",
] as const;
